/**
 *  MapComponent script file
**/
import Vue from 'vue'
import * as mapbox from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { mapGetters, mapActions } from 'vuex'
import { GETTERS, ACTIONS } from '@/store'
import saveActions from '@/mixins/saveActions'
import getRoutePoint from '@/mixins/getRoutePoint'
import { LAYERS_ID, LAYER_CONTOUR, LAYER_CONTOUR_MARKER, LAYER_BUILDING_FLOOR, LAYER_POIS, LAYER_POIS_SELECTED, UNCATEGORIZED_POIS } from '@/config'
import { ContextMenu } from '@/components/popups/context-menu'
import setLocation from '@/mixins/setLocation'
import { isSpecialPois } from '@/utils/isSpecialPois'
import { getFilterRoute } from '@/utils/getFilterRoute'
import { point, nearestPoint, featureCollection } from '@turf/turf'
import { disableVueObserver } from '@/utils/disableVueObserver'
import { normalizeLowerCase } from '@/utils/matchString'
import setDeviceWidthClass from '@/mixins/setDeviceWidthClass'

export default {
  name: 'MapComponent',
  mixins: [saveActions, getRoutePoint, setLocation, setDeviceWidthClass],
  components: {},
  props: {
    full_airport_loaded: false,
    explorerMode: {
      type: Boolean,
      default: false
    },
    printMode: {
      type: Boolean,
      default: false
    },
    lat: {
      require: false,
      type: String,
      default: ''
    },
    lon: {
      require: false,
      type: String,
      default: ''
    }
  },
  data () {
    return {
      map: undefined,
      mapLoaded: false,
      flying: false,
      loaded: false,
      popup: undefined,
      mapCanvas: undefined,
      contextMenuMarker: undefined,
      contextMenuMarkerSelected: [],
      contextMenuPopup: undefined,
      popupActive: false,
      debugMode: process.env.VUE_APP_DEBUG_MODE === 'true',
      buildingId: this.$root.$data.building,
      floorId: this.$root.$data.floor
    }
  },
  async mounted () {
    const map = new mapbox.Map({
      container: this.id,
      style: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
      center: [0, 0],
      zoom: 2
    })

    map.fitBounds(this.initialBbox)

    const nav = new mapbox.NavigationControl({ showCompass: false })
    map.addControl(nav, 'bottom-left')
    disableVueObserver(map)
    this.map = map

    document.querySelectorAll('.mapboxgl-ctrl').forEach(control => control.setAttribute('aria-hidden', true))

    const markers = require(`@/assets/sprite/markers@1x.png`)
    const json = Object.entries(require(`@/assets/sprite/markers@1x.json`))

    await new Promise(resolve => {
      const image = new Image()
      image.src = markers
      image.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = image.width
        canvas.height = image.height
        canvas.getContext('2d').drawImage(image, 0, 0, image.width, image.height)
        json.forEach(item => {
          const data = canvas.getContext('2d').getImageData(item[1].x, item[1].y, item[1].width, item[1].height).data
          this.map.addImage(item[0], { width: item[1].width, height: item[1].height, data: data }, { pixelRatio: window.devicePixelRatio })
        })
        resolve()
      }
    })

    this.map.on('load', async () => {
      this.mapLoaded = true

      if (this.lat !== '' && this.lon !== '') {
        const coordinates = [this.lon, this.lat]
        this[ACTIONS.SET_COORDINATES]({
          coordinates: coordinates,
          zoom: 19
        })
      }
    })

    this.map.on('movestart', () => {
      this.removeContextMenu()
    })

    this.map.on('click', () => {
      this.removeContextMenu()
    })

    this.map.on('moveend', () => {
      if (this.flying) {
        this.flying = false
        if (this.existLayer(LAYERS_ID.POI_LIST) && this.floor) {
          this.map.setLayoutProperty(LAYERS_ID.POI_LIST, 'visibility', 'visible')
        }
      }
    })

    this.map.on('zoomend', () => {
      const currentZoom = this.map.getZoom()
      if (!this.floor && currentZoom >= process.env.VUE_APP_AUTO_FLOOR_ZOOM_MAX) {
        this[ACTIONS.BLOCK_BBOX](true)
        const bbox = this.map.getBounds()
        const mapCentroidPoint = point(bbox.getCenter().toArray())

        const points = featureCollection(this.buildings.map(x => {
          return point(x.centroid.coordinates, {
            id: x.id,
            floor: x.default_level
          })
        }))

        const building = nearestPoint(mapCentroidPoint, points)
        if (!this.poi || building.properties.id === this.poi.properties.building) {
          this.saveFloor({ buildingId: building.properties.id, floorId: building.properties.floor, airportID: this.airportID })
        }
      } else if (this.mapLoaded && this.floor && this.map.getZoom() < process.env.VUE_APP_AUTO_FLOOR_ZOOM_MIN && !this.isRouteMode && !this.poi) {
        this[ACTIONS.LOAD_FLOOR]()
      }
    })

    this.map.on('contextmenu', (e) => {
      if ((this.floor && this.airport.has_graph && !this.printMode) || (this.floor && this.debugMode && !this.printMode)) {
        this.removeContextMenu()
        this.addContextMenuPoint(e)
      }
    })

    window.addEventListener('resize', this.resize)
  },
  destroyed () {
    window.removeEventListener('resize', this.resize)
    this.map.remove()
  },
  computed: {
    ...mapGetters({
      isMobile: GETTERS.GET_IS_MOBILE,
      id: GETTERS.GET_ID,
      bbox: GETTERS.GET_BBOX,
      coordinates: GETTERS.GET_COORDINATES,
      zoom: GETTERS.GET_ZOOM,
      accessToken: GETTERS.GET_ACCESS_TOKEN,
      buildings: GETTERS.GET_BUILDINGS,
      layerTree: GETTERS.GET_LAYER_TREE,
      floor: GETTERS.GET_FLOOR,
      building: GETTERS.GET_BUILDING,
      // category: GETTERS.GET_CATEGORY,
      poisCatalog: GETTERS.GET_POIS_CATALOG,
      poiId: GETTERS.GET_POI_ID,
      poiDId: GETTERS.GET_POID_ID,
      routeConfig: GETTERS.GET_ROUTE_CONFIG,
      poi: GETTERS.GET_POI,
      isRouteMode: GETTERS.IS_ROUTE_MODE,
      hasRoute: GETTERS.HAS_ROUTE,
      airportID: GETTERS.GET_AIRPORT_ID,
      airport: GETTERS.GET_AIRPORT,
      initialBbox: GETTERS.GET_INITIAL_BBOX,
      homePosition: GETTERS.GET_HOME_POSITION,
      filtering: GETTERS.GET_FILTERING,
      activeFilters: GETTERS.GET_ACTIVE_FILTERS,
      zonaubicacion: GETTERS.GET_ZONAUBICACION
    }),
    mapAirportLoaded () {
      return this.mapLoaded && this.full_airport_loaded
    },
    filterPois () {
      const filter = [
        'all'
      ]

      if (this.building) {
        filter.push(['==', ['get', 'building'], this.building.id])
      }
      if (this.floor) {
        filter.push(['==', ['get', 'level'], this.floor.id])
      }

      if (this.filtering) {
        const subcategories = []
        if (this.activeFilters.categories) {
          subcategories.push(...this.activeFilters.categories.subcategories)
        }
        if (this.activeFilters.subcategories.length) {
          subcategories.push(...this.activeFilters.subcategories)
        }
        if (subcategories.length) {
          filter.push(['any', ...subcategories.map(x => ['in', x, ['get', 'subcategories']])])
        }
        if (this.zonaubicacion) {
          filter.push(['==', ['get', 'pat_zonaubicacion', ['get', 'attrs']], this.zonaubicacion])
        }
      }
      return filter
    }
  },
  methods: {
    ...mapActions([
      ACTIONS.BLOCK_BBOX,
      ACTIONS.ADD_LAYER,
      ACTIONS.DELETE_LAYER,
      ACTIONS.SET_COORDINATES,
      ACTIONS.LOAD_TILES_BUILDING_FLOOR,
      ACTIONS.LOAD_FLOOR,
      ACTIONS.SET_HOME_POSITION,
      ACTIONS.LOAD_FLOOR
      // ACTIONS.LOAD_POIS_LIST
    ]),
    loadAirport () {
      this.loaded = true
      this.canvasMap = this.map.getCanvasContainer()
      // this.$emit('loaded', this.map)
      this.$root.$emit('mapLoaded', this.map)
      this.resize()

      this.addPoisLayer()

      if (this.bbox) {
        this.fitBounds(this.bbox)
      }

      if (this.buildings.length) {
        this.addBuildings(this.buildings)
      }

      if (this.floor || this.category) {
        this.setFloor(this.floor)
      }

      if (this.poiId) {
        this.setPoi(this.poiId)
      }

      if (this.routeConfig) {
        this.setContextualRoute()
      }
    },
    resize () {
      const inc = 20
      let i = 0
      const si = window.setInterval(() => {
        i += inc
        this.map.resize()
        if (i >= 250) { window.clearInterval(si) }
      }, inc)
    },
    getMap: function (found) {
      const checkForMap = () => {
        if (this.loaded) {
          found()
        } else {
          setTimeout(checkForMap, 50)
        }
      }
      checkForMap()
    },
    fitBounds (value) {
      this.flying = true
      if (this.existLayer(LAYERS_ID.POI_LIST)) {
        this.map.setLayoutProperty(LAYERS_ID.POI_LIST, 'visibility', 'none')
      }
      const pointsWrapper = document.getElementById('points-wrapper') || {}
      this.map.fitBounds(value, {
        padding: {
          left: this.isMobile ? 0 : pointsWrapper.offsetWidth || 0,
          bottom: 64,
          top: 64,
          right: 64
        },
        duration: 1000
      })
    },
    flyTo (value) {
      this.map.flyTo({
        ...value,
        speed: 2.4
      })
      this[ACTIONS.SET_COORDINATES]({
        coordinates: undefined
      })
    },
    getLayer (id) {
      return this.map.getLayer(id)
    },
    setLayoutProperty (id, visibility) {
      return this.map.setLayoutProperty(id, 'visibility', visibility)
    },
    existLayer (id) {
      return !!this.getLayer(id)
    },
    addLayerHandle ({ id, source, data, url, layers }) {
      if (source === 'raster') {
        this.addLayerRaster({ id, source, url })
      } else if (source === 'mvt') {
        this.addLayerMVT({ id, source, url, layers })
      } else {
        this.addLayerVector({ id, source, data, url, layers })
      }
    },
    addLayerRaster ({ id, source, url }) {
      this.map.addSource(id, {
        type: source,
        tiles: [url],
        tileSize: 256
      })
      this.map.addLayer({
        id: id,
        type: source,
        source: id
      })

      this.map.moveLayer(id, LAYERS_ID.POI_LIST)
      if (this.existRouteLayer()) {
        this.moveRouteLayer()
      }
    },
    addLayerMVT ({ id, source, url, layers }) {
      this.map.addSource(id, {
        type: 'vector',
        tiles: url
      })
      layers.forEach(layer => {
        this.map.addLayer({
          source: id,
          ...layer
        }, this.existLayer(LAYERS_ID.POI_LIST) ? LAYERS_ID.POI_LIST : null)
      })

      // this.map.moveLayer(id, LAYERS_ID.POI_LIST)
      if (this.existRouteLayer()) {
        this.moveRouteLayer()
      }
    },
    addLayerVector ({ id, source, url, data, layers = [] }) {
      if (this.existLayer(id)) {
        layers.forEach(layer => {
          this.map.removeLayer(layer.id || id)
        })
        this.map.removeSource(id)
      }

      const paramsSource = {
        type: source || 'vector'
      }
      const paramsLayer = {
      }

      if (url) {
        paramsSource.url = url
        paramsLayer['source-layer'] = id
      }

      if (data) {
        paramsSource.data = data
        paramsLayer['source'] = id
      }

      this.map.addSource(id, paramsSource)
      layers.forEach(layer => {
        this.map.addLayer({
          ...paramsLayer,
          ...layer,
          id: layer.id || id
        })
      })
      if (!layers.length) {
        this.map.addLayer(paramsLayer)
      }

      if (this.existLayer(LAYERS_ID.POI_LIST)) {
        this.map.moveLayer(LAYERS_ID.POI_LIST)
      }

      if (this.existLayer(LAYERS_ID.POI_LIST_SELECTED)) {
        this.map.moveLayer(LAYERS_ID.POI_LIST_SELECTED)
      }
    },
    async addContextMenuPoint (e) {
      const el = document.createElement('div')
      el.className = `context-menu-marker`

      this.contextMenuMarker = new mapbox.Marker(el)
        .setLngLat(e.lngLat)
        .addTo(this.map)

      let componentConfig = null

      try {
        componentConfig = ContextMenu
      } catch (err) {
        // eslint-disable-next-line
        console.error(`[showPopup] Error importing component "context-menu" \n`, err)
        return
      }

      const ComponentClass = Vue.extend({ ...componentConfig, i18n: this.$i18n })
      const popupId = 'context-menu'

      const componentInstance = new ComponentClass({
        propsData: {
          coordinates: [
            e.lngLat.lng,
            e.lngLat.lat
          ],
          origin: this.origin,
          destination: this.destination,
          building: this.building.id,
          floorName: this.floor.name,
          floor: this.floor.id,
          loadRoute: this[ACTIONS.LOAD_ROUTE],
          addContextMenuMarkerSelected: this.addContextMenuMarkerSelected,
          setLocation: this.setLocation,
          hasGraph: this.airport.has_graph
        }
      })

      this.contextMenuPopup = new mapbox.Popup({
        closeButton: false,
        closeOnClick: true,
        anchor: 'top-left',
        offset: 12,
        className: 'context-menu'
      })
        .setLngLat(e.lngLat)
        .setHTML(`<div id="${popupId}"></div>`)
        .addTo(this.map)

      this.contextMenuPopup.vue = componentInstance
      componentInstance.$mount(`#${popupId}`)
      this.contextMenuPopup._update()

      componentInstance.$on('close', () => {
        this.removeContextMenu()
      })
    },
    addContextMenuMarkerSelected (step) {
      const el = document.createElement('div')
      el.className = 'context-menu-marker-selected'

      const marker = new mapbox.Marker(el, {
        anchor: 'bottom'
      })
        .setLngLat(step.geometry.coordinates)
        .addTo(this.map)
      this.contextMenuMarkerSelected.push(marker)
    },
    removeContextMenu () {
      if (this.contextMenuMarker) {
        this.contextMenuMarker.remove()
        this.contextMenuMarker = undefined
      }
      if (this.contextMenuPopup) {
        this.contextMenuPopup.vue.$destroy()
        this.contextMenuPopup.remove()
        this.contextMenuPopup = undefined
      }
    },
    removeContextMenuSelected () {
      this.contextMenuMarkerSelected.forEach(marker => {
        marker.remove()
      })
      this.contextMenuMarkerSelected = []
    },
    async addBuildings (buildings) {
      const contour = buildings.reduce((acum, current) => {
        const contour = this.generateGeoJsonBuilding(current)

        acum.features.push(...contour)

        return acum
      }, {
        type: 'FeatureCollection',
        features: []
      })

      const layer = LAYER_CONTOUR

      layer.data = contour

      layer.layers[0].filter = true
      if (this.building) {
        layer.layers[0].filter = ['!=', ['get', 'id'], this.building.id]
      }

      this[ACTIONS.ADD_LAYER]({
        ...layer
      })

      const contourCentroid = buildings.reduce((acum, current) => {
        const contour = this.generateGeoJsonBuildingMarker(current)

        acum.features.push(contour)

        return acum
      }, {
        type: 'FeatureCollection',
        features: []
      })

      // if (document.fonts && document.fonts.ready) {
      //   await document.fonts.ready
      // }

      await this.addMarkers()

      const layerContour = LAYER_CONTOUR_MARKER
      layerContour.data = contourCentroid
      layerContour.layers[0].filter = true

      if (this.building) {
        layerContour.layers[0].filter = ['!=', ['get', 'id'], this.building.id]
      }

      this[ACTIONS.ADD_LAYER]({
        ...layerContour
      })

      if (this.explorerMode) {
        this.map.on('click', LAYER_CONTOUR.id, (e) => {
          const feature = e.features[0]

          if (feature.properties.floor !== undefined) {
            this.saveFloor({ buildingId: feature.properties.id, floorId: feature.properties.floor, airportID: this.airportID, tag: 'mapa', event: true })
          } else {
            // eslint-disable-next-line
            console.error('No default level. ID: ' + feature.properties.id)
          }
        })
        this.map.on('mousemove', LAYER_CONTOUR.id, (e) => {
          if (!this.popupActive) {
            this.canvasMap.style.cursor = 'pointer'
            this.removePopup()
            const feature = e.features[0]
            this.canvasMap.style.cursor = 'pointer'
            const coordinates = e.lngLat
            const description = feature.properties.name

            this.popup = new mapbox.Popup({
              closeButton: false,
              anchor: 'bottom'
            })

            this.popup
              .setLngLat(coordinates)
              .setHTML(description)
              .addTo(this.map)
          }
        })
        this.map.on('mouseleave', LAYER_CONTOUR.id, (e) => {
          this.canvasMap.style.cursor = ''
          this.removePopup()
        })
        this.map.on('mousemove', LAYERS_ID.BUILDING_FLOOR[24], () => {
          if (!this.popupActive) {
            this.canvasMap.style.cursor = ''
            this.removePopup()
          }
        })
        this.map.on('mousemove', LAYERS_ID.BUILDING_FLOOR[18], () => {
          if (!this.popupActive) {
            this.canvasMap.style.cursor = ''
            this.removePopup()
          }
        })

        this.map.on('click', LAYER_CONTOUR_MARKER.id, (e) => {
          const feature = e.features[0]

          if (feature.properties.floor !== undefined) {
            this.saveFloor({ buildingId: feature.properties.id, floorId: feature.properties.floor, airportID: this.airportID })
          } else {
            // eslint-disable-next-line
            console.error('No default level. ID: ' + feature.properties.id)
          }
        })
        this.map.on('mousemove', LAYER_CONTOUR_MARKER.id, (e) => {
          this.canvasMap.style.cursor = 'pointer'
          this.removePopup()
          const feature = e.features[0]
          this.canvasMap.style.cursor = 'pointer'
          const coordinates = e.lngLat
          const description = feature.properties.name

          this.popup = new mapbox.Popup({
            closeButton: false,
            anchor: 'bottom'
          })

          this.popup
            .setLngLat(coordinates)
            .setHTML(description)
            .addTo(this.map)
        })
        this.map.on('mouseleave', LAYER_CONTOUR_MARKER.id, (e) => {
          this.canvasMap.style.cursor = ''
          this.removePopup()
        })
      }
    },
    generateGeoJsonBuildingMarker (building) {
      return {
        type: 'Feature',
        properties: {
          category: building.category,
          floor: building.default_level,
          group_id: building.group_id,
          priority: building.priority,
          id: building.id,
          name: building.name,
          short_name: building.short_name,
          poi_name: building.short_name + building.color,
          color: building.color,
          fontColor: building.font_color
        },
        geometry: {
          type: building.centroid.type,
          coordinates: building.centroid.coordinates
        }
      }
    },
    generateGeoJsonBuilding (building) {
      return building.bounds.geometries.map(g => {
        return {
          type: 'Feature',
          properties: {
            category: building.category,
            floor: building.default_level,
            group_id: building.group_id,
            priority: building.priority,
            id: building.id,
            name: building.name,
            short_name: building.short_name,
            color: building.color
          },
          geometry: {
            type: g.type,
            coordinates: g.coordinates
          }
        }
      })
    },
    addMarkers () {
      const pixelRatio = window.devicePixelRatio === 1 ? 1 : 2
      this.buildings.filter(x => x.id !== (this.building || {}).id).forEach(building => {
        const poiName = building.short_name + building.color
        if (!this.map.hasImage(poiName)) {
          const isParking = building.category === 'PARKING PUBLICO'
          const canvas = document.createElement('canvas')
          const size = (!isParking ? 40 : 24) * pixelRatio
          canvas.width = size
          canvas.height = size
          const ctx = canvas.getContext('2d')
          ctx.imageSmoothingEnabled = false
          ctx.globalAlpha = 1

          let radius = canvas.height / 2
          ctx.translate(radius, radius)
          radius = radius - 8

          ctx.shadowColor = '#999'
          ctx.shadowBlur = 8

          if (!isParking) {
            ctx.arc(0, 0, radius, 0, 2 * Math.PI)
            ctx.fillStyle = building.color
            ctx.fill()
          } else {
            const borderRadius = 4
            const margin = 2
            const x = size / 2 - margin
            ctx.moveTo(borderRadius - x, 0 - x)
            ctx.lineTo(x - borderRadius, 0 - x)
            ctx.arc(x - borderRadius, 0 - x + borderRadius, borderRadius, 1.5 * Math.PI, 0.2 * Math.PI)
            ctx.moveTo(x, 0 - x + borderRadius + 2)
            ctx.lineTo(x, x - borderRadius)
            ctx.arc(x - borderRadius, x - borderRadius, borderRadius, 0 * Math.PI, 0.5 * Math.PI)
            ctx.moveTo(x - borderRadius, x)
            ctx.lineTo(borderRadius - x, x)
            ctx.arc(0 - x + borderRadius, x - borderRadius, borderRadius, 0.5 * Math.PI, 1 * Math.PI)
            ctx.moveTo(0 - x, x - borderRadius)
            ctx.lineTo(0 - x, 0 - x + borderRadius)
            ctx.arc(borderRadius - x, borderRadius - x, borderRadius, 1 * Math.PI, 1.5 * Math.PI)
            ctx.fillStyle = building.color
            ctx.fillRect(0 - x + 0.7, 0 - x + 0.5, size - borderRadius - 1, size - borderRadius - 1.5)
          }

          ctx.shadowColor = 'transparent'
          ctx.shadowBlur = 0

          ctx.lineWidth = pixelRatio
          ctx.strokeStyle = building.font_color
          ctx.stroke()
          ctx.beginPath()

          ctx.font = `600 ${12 * pixelRatio}px Source Sans Pro`
          ctx.textAlign = 'center'
          ctx.fillStyle = building.font_color
          ctx.fillText(building.short_name, 0, 0 + (4 * pixelRatio))

          const data = canvas.getContext('2d').getImageData(0, 0, size, size).data
          this.map.addImage(poiName, { width: size, height: size, data: data }, { pixelRatio: pixelRatio })
        }
      })
    },
    setSelectedPoi (id) {
      this.removeContextMenuSelected()
      if (this.existLayer(LAYERS_ID.POI_LIST_SELECTED)) {
        const data = {
          'type': 'FeatureCollection',
          'features': []
        }

        if (id) {
          let poi = this.poisList.features.find(x => x.properties.id === id)
          if (!poi) {
            poi = this.uncategorizedPoisList.features.find(x => x.properties.id === id)
          }
          data.features.push(poi)
        }

        if (this.floor) {
          this.steps.filter(step => !step.new).forEach(step => {
            if (step.mode !== 'point') {
              data.features.push(step)
            } else {
              this.addContextMenuMarkerSelected(step)
            }
          })
        }

        // TODO: Do when nearestPoint is specked
        // if (this.hasRoute) {
        //   this.route.forEach(r => {
        //     r.instructions.forEach(step => {
        //       const def = ROUTING_ACTIONS[step.type]
        //       if (def.changeFloor) {
        //         const feature = r.geometry.features[step.geometryindex]
        //         feature.geometry.coordinates = feature.geometry.coordinates.slice(0, 2)
        //         const pois = this.uncategorizedPoisList
        //         pois.features = pois.features.filter(x => x.properties.level === this.floor.id)
        //         const poi = nearestPoint(feature, pois)
        //         if (poi) {
        //           data.features.push(poi)
        //         }
        //       }
        //     })
        //   })
        // }

        this.map.getSource(LAYERS_ID.POI_LIST_SELECTED).setData(data)
        this.map.setLayoutProperty(LAYERS_ID.POI_LIST_SELECTED, 'visibility', data.features.length ? 'visible' : 'none')
        this.map.moveLayer(LAYERS_ID.POI_LIST_SELECTED)
      }
    },
    setPoi (id) {
      this.savePoi({ poiId: id, airportID: this.airportID, tag: 'mapa' })
      this.setSelectedPoi(id)
    },
    existRouteLayer () {
      return this.existLayer(LAYERS_ID.ROUTES[0]) || this.existLayer(LAYERS_ID.ROUTES[1]) || this.existLayer(LAYERS_ID.ROUTES[2])
    },
    moveRouteLayer () {
      this.map.moveLayer(LAYERS_ID.ROUTES[0])
      this.map.moveLayer(LAYERS_ID.ROUTES[1])
      this.map.moveLayer(LAYERS_ID.ROUTES[2])
    },
    setRouteFloor (visibility) {
      if (this.existRouteLayer()) {
        this.moveRouteLayer()

        this.map.setLayoutProperty(LAYERS_ID.ROUTES[0], 'visibility', visibility ? 'visible' : 'none')
        this.map.setLayoutProperty(LAYERS_ID.ROUTES[1], 'visibility', visibility ? 'visible' : 'none')
        this.map.setLayoutProperty(LAYERS_ID.ROUTES[2], 'visibility', visibility ? 'visible' : 'none')

        const { layerOpacity, arrowOpacity } = getFilterRoute(this.building, this.floor)

        const filter = [
          'all',
          ['==', ['get', 'level'], this.floor.id],
          ['==', ['get', 'building'], this.building.id]
        ]

        this.map.setFilter(LAYERS_ID.ROUTES[1], filter)
        this.map.setPaintProperty(LAYERS_ID.ROUTES[0], 'line-opacity', layerOpacity)
        this.map.setPaintProperty(LAYERS_ID.ROUTES[2], 'icon-opacity', arrowOpacity)

        this.setSelectedPoi()
      }
    },
    async setFloor (floor) {
      if (this.existLayer(LAYERS_ID.TILES)) {
        this.map.removeLayer(LAYERS_ID.TILES)
        this.map.removeSource(LAYERS_ID.TILES)
        this[ACTIONS.DELETE_LAYER](LAYERS_ID.TILES)
      }
      if (this.existLayer(LAYERS_ID.POI_LIST)) {
        this.map.setLayoutProperty(LAYERS_ID.POI_LIST, 'visibility', 'none')
      }

      if (floor) {
        if (this.existLayer(LAYERS_ID.BUILDING_CONTOUR)) {
          this.map.setFilter(LAYERS_ID.BUILDING_CONTOUR, ['!=', ['get', 'id'], this.building.id])
        }
        if (this.existLayer(LAYERS_ID.BUILDING_CONTOUR_MARKER)) {
          this.map.setFilter(LAYERS_ID.BUILDING_CONTOUR_MARKER, ['!=', ['get', 'id'], this.building.id])
        }

        const buildingFloorFilter = [
          'all',
          ['==', ['get', 'building'], ['to-string', this.building.id]],
          ['==', ['get', 'height'], this.floor.id]
        ]
        if (!this.existLayer(LAYER_BUILDING_FLOOR.id)) {
          const layerBuildingFloor = { ...LAYER_BUILDING_FLOOR }
          layerBuildingFloor.layers.forEach(l => {
            l.filter = buildingFloorFilter
          })
          await this[ACTIONS.LOAD_TILES_BUILDING_FLOOR](LAYER_BUILDING_FLOOR)
        } else {
          LAYER_BUILDING_FLOOR.layers.forEach(l => {
            this.map.setFilter(l.id, buildingFloorFilter)
          })
        }

        if (this.existLayer(LAYERS_ID.POI_LIST)) {
          this.map.setFilter(LAYERS_ID.POI_LIST, this.filterPois)
          this.map.setLayoutProperty(LAYERS_ID.POI_LIST, 'visibility', 'visible')
        }
      } else {
        if (this.existLayer(LAYER_BUILDING_FLOOR.id)) {
          LAYER_BUILDING_FLOOR.layers.forEach(l => {
            this.map.setFilter(l.id, ['to-boolean', 0])
          })
        }
        if (this.existLayer(LAYERS_ID.BUILDING_CONTOUR)) {
          this.map.setFilter(LAYERS_ID.BUILDING_CONTOUR, true)
        }
        if (this.existLayer(LAYERS_ID.BUILDING_CONTOUR_MARKER)) {
          this.map.setFilter(LAYERS_ID.BUILDING_CONTOUR_MARKER, true)
        }
        if (this.existLayer(LAYERS_ID.POI_LIST)) {
          this.map.setLayoutProperty(LAYERS_ID.POI_LIST, 'visibility', 'none')
        }
      }

      this.setRouteFloor(true)
    },
    async addPoisLayer () {
      const layer = LAYER_POIS
      // const data = this.category ? this.filterCategoryPois : this.poisList
      const data = this.poisList
      layer.data = { ...data }
      layer.data.features = [...layer.data.features, ...this.uncategorizedPoisList.features]
      layer.layers[0].filter = this.filterPois
      layer.layers[0].layout.visibility = this.floor ? 'visible' : 'none'
      // layer.layers[1].layout.visibility = this.poi ? 'visible' : 'none'

      this[ACTIONS.ADD_LAYER]({
        ...layer
      })

      this[ACTIONS.ADD_LAYER](LAYER_POIS_SELECTED)

      if (this.explorerMode) {
        this.map.on('click', LAYERS_ID.POI_LIST, (e) => {
          const feature = e.features[0]
          const id = feature.properties.id
          const subcategories = JSON.parse(feature.properties.subcategories)
          let poi = this.poisList.features.find(x => x.properties.id === id)
          if (!poi) {
            poi = this.uncategorizedPoisList.features.find(x => x.properties.id === id)
          }
          if (!this.isRouteMode) {
            this.setPoi(id)

            const label = `${this.building.name}. Planta ${poi.properties.levelName}. ${this.$t(poi.properties.attrs.pat_nombre)}`
            const tag = {
              'eventCategory': 'mapa',
              'eventAction': 'clic',
              'eventLabel': normalizeLowerCase(label),
              'language': this.$i18n.locale,
              'airportID': this.airportID
            }

            window.digitalChange('event', tag)
          } else {
            const destinationName = (this.destinationName || '').includes('step') ? 'step' : this.destinationName
            const name = destinationName || (this.destination ? 'origin' : 'destination')
            this.setLocation([feature])
            this[ACTIONS.LOAD_ROUTE]({
              i18n: this.$i18n,
              [name]: {
                geometry: {
                  coordinates: [
                    e.lngLat.lng,
                    e.lngLat.lat
                  ],
                  type: 'Point'
                },
                properties: {
                  ...feature.properties,
                  attrs: JSON.parse(feature.properties.attrs),
                  subcategories
                },
                type: 'Feature'
              },
              routeMode: true
            })
          }
        })
      }

      this.map.on('mouseover', LAYERS_ID.POI_LIST, (e) => {
        this.popupActive = true
        this.setPoiTooltip(e)
      })

      this.map.on('mouseleave', LAYERS_ID.POI_LIST, () => {
        this.popupActive = false
        this.removePopup()
      })

      this.map.on('mouseleave', LAYERS_ID.POI_LIST_SELECTED, () => {
        this.removePopup()
      })

      this.map.on('mouseover', LAYERS_ID.POI_LIST_SELECTED, (e) => {
        this.setPoiTooltip(e, true)
      })
    },
    removePopup () {
      if (this.popup) {
        this.popup
          .remove()
      }
    },
    setPoiTooltip (e, isSelected) {
      this.removePopup()
      const feature = e.features[0]
      const subcategories = JSON.parse(feature.properties.subcategories)
      const specialPoi = UNCATEGORIZED_POIS[isSpecialPois(subcategories)]
      this.canvasMap.style.cursor = 'pointer'
      const coordinates = feature.geometry.coordinates.slice()
      const description = specialPoi ? this.$t(specialPoi.name) : JSON.parse(feature.properties.attrs).pat_nombre

      this.popup = new mapbox.Popup({
        closeButton: false,
        anchor: 'bottom',
        offset: {
          bottom: [0, isSelected ? -25 : -10]
        }
      })

      this.popup
        .setLngLat(coordinates)
        .setHTML(description)
        .addTo(this.map)
    },
    setContextualRoute () {
      const points = this.poisList.features.filter(point => this.routeConfig.steps.includes(point.properties.id))
      this.setLocation(points)

      this[ACTIONS.MOVE_ROUTE]({ steps: points })
    },
    clearRoute () {
      if (this.existRouteLayer()) {
        this.map.removeLayer(LAYERS_ID.ROUTES[0])
        this.map.removeLayer(LAYERS_ID.ROUTES[1])
        this.map.removeLayer(LAYERS_ID.ROUTES[2])
        this.map.removeSource(LAYERS_ID.ROUTES[0])
        this.map.removeSource(LAYERS_ID.ROUTES[2])
        this[ACTIONS.DELETE_LAYER](LAYERS_ID.ROUTES[0])
        this[ACTIONS.DELETE_LAYER](LAYERS_ID.ROUTES[2])
      }

      this.setSelectedPoi()
    }
  },
  watch: {
    mapAirportLoaded (loaded) {
      if (loaded) {
        this.loadAirport()
      }
    },
    bbox (value) {
      if (value) {
        this.fitBounds(value)
      }
    },
    accessToken (value) {
      mapbox.accessToken = value
    },
    layerTree (value) {
      value.forEach(l => {
        if (!this.existLayer(l.id)) {
          this.addLayerHandle(l)
        }
      })
    },
    buildings (value) {
      if (this.loaded) {
        this.addBuildings(value)
      }
    },
    floor (value) {
      if (this.loaded) {
        this.setFloor(value)
      }
    },
    coordinates (value) {
      if (value) {
        this.flyTo({
          center: value,
          zoom: this.zoom
        })
        if (this.lat !== '' && this.lon !== '') {
          const coordinates = [this.lon, this.lat]
          const el = document.createElement('div')
          const description = 'YOU ARE HERE'
          const popup = new mapbox.Popup({ closeButton: true, anchor: 'bottom' })
          el.className = `context-menu-marker`
          setTimeout(() => {
            popup.setLngLat(coordinates).setHTML(description).addTo(this.map)
            new mapbox.Marker(el).setPopup(popup).setLngLat(coordinates).addTo(this.map).togglePopup()
          }, 1500)
        }
      }
    },
    poi (value) {
      this.setSelectedPoi(value && value.properties.id)
      if (value) {
        this[ACTIONS.SET_COORDINATES]({
          coordinates: value.geometry.coordinates,
          zoom: 19
        })
      }
    },
    steps () {
      this.setSelectedPoi()
    },
    isRouteMode (value) {
      if (!value) {
        this.clearRoute()
      }
    },
    hasRoute (value) {
      if (!value) {
        this.clearRoute()
      } else {
        this.setSelectedPoi()
      }
    },
    homePosition (value) {
      if (value) {
        if (this.homePosition) {
          const poiObject = this.poisList.features.find(x => x.properties.id === this.homePosition)
          const building = this.buildings.find(x => x.id === poiObject.properties.building)
          const floor = building.levels.find(x => x.id === this.floorId)
          const coordinates = [poiObject.geometry.coordinates[0], poiObject.geometry.coordinates[1]]
          this[ACTIONS.SET_COORDINATES]({
            coordinates: coordinates,
            zoom: 19
          })
          this[ACTIONS.LOAD_FLOOR]({
            floor: floor,
            building: building
          })
          this.setPoi(this.homePosition)
          this[ACTIONS.SET_HOME_POSITION](false)
        }
      }
    },
    filtering: {
      // immediate: true,
      handler () {
        if (this.existLayer(LAYERS_ID.POI_LIST)) {
          this.map.setFilter(LAYERS_ID.POI_LIST, this.filterPois)
        }
      }
    },
    activeFilters: {
      deep: true,
      handler () {
        if (this.existLayer(LAYERS_ID.POI_LIST)) {
          this.map.setFilter(LAYERS_ID.POI_LIST, this.filterPois)
        }
      }
    }
  }
}
